import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Loader2, Play, PhoneCall, CalendarCheck, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';

export const RecordingPlayer = ({ callId }) => {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  const fetchIt = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/calls/live/${callId}/recording`, { responseType: 'blob' });
      setUrl(URL.createObjectURL(data));
    } catch { toast.error('Recording not ready yet — try again in a minute'); }
    finally { setLoading(false); }
  };
  if (url) return <audio controls autoPlay src={url} className="w-full h-9" data-testid={`call-recording-audio-${callId}`} />;
  return (
    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={fetchIt} disabled={loading} data-testid={`call-recording-play-${callId}`}>
      {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1" />} Play recording
    </Button>
  );
};

const STATUS_TONE = {
  completed: 'bg-emerald-100 text-emerald-800', failed: 'bg-red-100 text-red-800',
  in_progress: 'bg-indigo-100 text-indigo-800', dialing: 'bg-amber-100 text-amber-800',
};

export const CallLogPanel = () => {
  const [calls, setCalls] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);
  const [f, setF] = useState({ from: '', to: '', q: '', booked: false });

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 300, from: f.from || undefined, to: f.to || undefined, q: f.q || undefined, booked: f.booked || undefined };
      const { data } = await api.get('/calls', { params });
      setCalls(data.calls || []); setTotal(data.total ?? (data.calls || []).length);
    }
    catch { toast.error('Failed to load calls'); }
    finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [f]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="border border-stone-200 rounded-lg bg-white overflow-hidden" data-testid="call-log-panel">
      <div className="px-3 py-2 border-b border-stone-200 flex items-center gap-2 flex-wrap">
        <PhoneCall className="w-4 h-4 text-rose-600" />
        <span className="text-sm font-semibold text-stone-900">AI Call Recordings</span>
        <span className="text-[11px] text-stone-500" data-testid="call-log-total">{total} calls</span>
        <div className="ml-auto flex items-center gap-1.5 flex-wrap">
          <input type="date" value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} className="h-7 text-xs border border-stone-300 rounded px-1.5" data-testid="call-log-from" />
          <span className="text-[11px] text-stone-400">–</span>
          <input type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} className="h-7 text-xs border border-stone-300 rounded px-1.5" data-testid="call-log-to" />
          <input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Patient, number, clinic, staff…" className="h-7 w-48 text-xs border border-stone-300 rounded px-2" data-testid="call-log-search" />
          <label className="text-[11px] text-stone-600 flex items-center gap-1"><input type="checkbox" checked={f.booked} onChange={(e) => setF({ ...f, booked: e.target.checked })} data-testid="call-log-booked" /> Booked only</label>
          <Button size="sm" variant="outline" className="h-7" onClick={load} data-testid="call-log-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>
      <ul className="divide-y divide-stone-100">
        {calls.length === 0 && !loading && <li className="p-4 text-xs text-stone-400">No calls yet.</li>}
        {calls.map((c) => <CallRow key={c.id} c={c} open={open === c.id} onToggle={() => setOpen(open === c.id ? null : c.id)} />)}
      </ul>
    </div>
  );
};

function CallRow({ c, open, onToggle }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    if (open && !detail) api.get(`/calls/${c.id}`).then((r) => setDetail(r.data)).catch(() => {});
  }, [open, detail, c.id]);
  const d = detail || c;
  return (
    <li data-testid={`call-row-${c.id}`}>
      <button type="button" onClick={onToggle} className="w-full px-3 py-2.5 flex items-center gap-3 text-left hover:bg-stone-50 transition-colors">
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-stone-900 truncate">{c.patient_name || 'Patient'} <span className="font-mono text-stone-500 font-normal">{c.to}</span></div>
          <div className="text-[11px] text-stone-500 truncate">{new Date(c.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })} · {c.duration ? `${c.duration}s` : '—'} · {c.mode === 'live' ? 'Live (Hinglish)' : 'Legacy'} · {c.initiated_by || ''}</div>
        </div>
        {d.booked_slot && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-600 text-white inline-flex items-center gap-1" data-testid={`call-booked-${c.id}`}>
            <CalendarCheck className="w-3 h-3" /> {new Date(d.booked_slot).toLocaleString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${STATUS_TONE[d.status] || 'bg-stone-100 text-stone-700'}`}>{d.status}</span>
        {open ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          {d.has_recording ? <RecordingPlayer callId={c.id} /> : <div className="text-[11px] text-stone-500 italic" data-testid={`call-no-recording-${c.id}`}>{c.mode === 'live' ? 'Recording is being saved — refresh in a minute.' : 'No recording — older-style call (before live calling).'}</div>}
          {d.summary && <div className="text-xs bg-indigo-50 border border-indigo-200 rounded p-2 text-stone-800"><b>Summary:</b> {d.summary}</div>}
          <div className="max-h-64 overflow-auto hc-scrollbar space-y-1 bg-stone-50 border border-stone-200 rounded p-2">
            {(detail?.turns || []).filter((t) => t.text).map((t, i) => (
              <div key={i} className={`text-xs ${t.direction === 'patient' ? 'text-stone-800' : 'text-rose-700'}`}>
                <b>{t.direction === 'patient' ? 'Patient' : 'Priya'}:</b> {t.text}
              </div>
            ))}
            {detail && !(detail.turns || []).some((t) => t.text) && <div className="text-xs text-stone-400">No transcript for this call.</div>}
            {!detail && <div className="text-xs text-stone-400">Loading transcript…</div>}
          </div>
        </div>
      )}
    </li>
  );
}
